/**
 * Campos de endereço para formulário de parceiro.
 * CEP aciona busca na ViaCEP e preenche automaticamente: endereco (logradouro), bairro, cidade, estado.
 * Usuário deve preencher obrigatoriamente: CEP, número.
 */
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MapPin } from 'lucide-react';

const formatCEP = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

export interface EnderecoParceiroValues {
  cep: string;
  endereco: string;
  numero: string;
  bairro: string;
  cidade: string;
  estado: string;
}

export interface EnderecoParceiroFormProps {
  values: EnderecoParceiroValues;
  onChange: (values: EnderecoParceiroValues) => void;
  disabled?: boolean;
  /** Prefixo para ids (ex: auth-parceiro, parceiro-form) */
  idPrefix?: string;
}

async function buscarEnderecoPorCEP(cep: string): Promise<{ logradouro?: string; bairro?: string; localidade?: string; uf?: string } | null> {
  const clean = cep.replace(/\D/g, '');
  if (clean.length !== 8) return null;
  try {
    const res = await fetch(`https://viacep.com.br/ws/${clean}/json/`);
    const data = await res.json();
    if (data.erro) return null;
    return {
      logradouro: data.logradouro || '',
      bairro: data.bairro || '',
      localidade: data.localidade || '',
      uf: data.uf || '',
    };
  } catch {
    return null;
  }
}

export function EnderecoParceiroForm({ values, onChange, disabled, idPrefix = 'parceiro' }: EnderecoParceiroFormProps) {
  const handleCEPChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '');
    const formatted = formatCEP(raw);
    onChange({ ...values, cep: formatted });
  };

  const handleCEPBlur = async () => {
    const clean = values.cep.replace(/\D/g, '');
    if (clean.length !== 8) return;
    const data = await buscarEnderecoPorCEP(clean);
    if (data) {
      onChange({
        ...values,
        endereco: data.logradouro || values.endereco,
        bairro: data.bairro || values.bairro,
        cidade: data.localidade || values.cidade,
        estado: data.uf || values.estado,
      });
    }
  };

  const handleFieldChange = (field: keyof EnderecoParceiroValues) => (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...values, [field]: e.target.value });
  };

  return (
    <div className="space-y-4">
      {/* CEP */}
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-cep`} className="flex items-center gap-2">
          <MapPin className="h-4 w-4" />
          CEP *
        </Label>
        <Input
          id={`${idPrefix}-cep`}
          value={values.cep}
          onChange={handleCEPChange}
          onBlur={handleCEPBlur}
          placeholder="00000-000"
          maxLength={9}
          disabled={disabled}
        />
        <p className="text-xs text-muted-foreground">Preencha o CEP para buscar endereço automaticamente</p>
      </div>

      {/* Endereço (logradouro) */}
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-endereco`}>Nome da rua *</Label>
        <Input
          id={`${idPrefix}-endereco`}
          value={values.endereco}
          onChange={handleFieldChange('endereco')}
          placeholder="Ex: Rua das Flores"
          disabled={disabled}
        />
      </div>

      {/* Número e Bairro */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-numero`}>Número *</Label>
          <Input
            id={`${idPrefix}-numero`}
            value={values.numero}
            onChange={handleFieldChange('numero')}
            placeholder="Ex: 123"
            disabled={disabled}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-bairro`}>Bairro *</Label>
          <Input
            id={`${idPrefix}-bairro`}
            value={values.bairro}
            onChange={handleFieldChange('bairro')}
            placeholder="Ex: Centro"
            disabled={disabled}
          />
        </div>
      </div>

      {/* Cidade e Estado */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-cidade`}>Cidade *</Label>
          <Input
            id={`${idPrefix}-cidade`}
            value={values.cidade}
            onChange={handleFieldChange('cidade')}
            placeholder="Ex: Florianópolis"
            disabled={disabled}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-estado`}>Estado (UF) *</Label>
          <Input
            id={`${idPrefix}-estado`}
            value={values.estado}
            onChange={handleFieldChange('estado')}
            placeholder="Ex: SC"
            maxLength={2}
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  );
}
